import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import {
  ChevronDown, ChevronUp, Calendar, Settings, CheckCircle2, Target,
  Plus, Minus, ChevronLeft, ChevronRight, GripVertical,
  Trash2, Clock, X, Check, RotateCcw, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useHabitTracker, getWeekEnd, formatMinutes, formatHours, formatCurrency, parseTimeInput, buildDynamicTitle, type Habit } from './useHabitTracker';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/AuthContext';
import { useWeeklyOne } from '@/features/weekly-one/WeeklyOneContext';
import posthog from '@/lib/posthog';
import { WeeklyOneInfoTooltip } from '@/components/ui/WeeklyOneInfoTooltip';

const WAT = {
  gold:      '#A07F4E',
  goldMid:   '#C09D6C',
  goldLight: '#D4B98C',
  border:    '#E5DED2',
  deep:      '#201917',
  brown2:    '#2D2421',
  brown3:    '#3A2F26',
  muted:     '#888070',
  success:   '#339966',
  creamBg:   '#F1ECE5',
} as const;

function WATRing({ value }: { value: number }) {
  const r = 24;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={62} height={62} viewBox="0 0 62 62" style={{ flexShrink: 0 }}>
      <circle cx="31" cy="31" r={r} stroke={WAT.creamBg} strokeWidth="5" fill="none" />
      <circle
        cx="31" cy="31" r={r}
        stroke="url(#watring)" strokeWidth="5" fill="none"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 31 31)"
      />
      <defs>
        <linearGradient id="watring" x1="0" x2="1">
          <stop offset="0"    stopColor={WAT.gold} />
          <stop offset="0.55" stopColor={WAT.goldMid} />
          <stop offset="1"    stopColor={WAT.goldLight} />
        </linearGradient>
      </defs>
    </svg>
  );
}

const UNIT_OPTIONS: { value: Habit['unit']; label: string }[] = [
  { value: 'count',    label: 'count' },
  { value: 'minutes',  label: 'minutes' },
  { value: 'hours',    label: 'hours' },
  { value: 'currency', label: 'currency ($)' },
  { value: 'yes_no',   label: 'yes / no' },
];

function unitSuffix(unit: Habit['unit']): string {
  switch (unit) {
    case 'minutes':  return 'm';
    case 'hours':    return 'h';
    case 'currency': return '$';
    case 'yes_no':   return '';
    default:         return 'x';
  }
}

function getDisplayCount(unit: Habit['unit'], count: number): string {
  switch (unit) {
    case 'minutes':  return formatMinutes(count);
    case 'hours':    return formatHours(count);
    case 'currency': return formatCurrency(count);
    case 'yes_no':   return count >= 1 ? 'Yes' : 'No';
    default:         return `${count}`;
  }
}

function getDisplayTarget(habit: Habit): string {
  switch (habit.unit) {
    case 'minutes':  return formatMinutes(habit.target_per_week);
    case 'hours':    return formatHours(habit.target_per_week);
    case 'currency': return formatCurrency(habit.target_per_week);
    case 'yes_no':   return '1';
    default:         return `${habit.target_per_week}`;
  }
}

const BURST_COLORS = ['hsl(var(--primary))', 'hsl(142 71% 45%)', 'hsl(38 92% 50%)', 'hsl(280 65% 60%)', 'hsl(200 80% 55%)'];

function CelebrationBurst({ show }: { show: boolean }) {
  const particles = useMemo(() =>
    Array.from({ length: 24 }, (_, i) => {
      const angle = (i / 24) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const distance = 80 + Math.random() * 100;
      return { id: i, x: Math.cos(angle) * distance, y: Math.sin(angle) * distance, color: BURST_COLORS[i % BURST_COLORS.length], size: 5 + Math.random() * 6, delay: Math.random() * 0.25 };
    }), []);

  return (
    <AnimatePresence>
      {show && particles.map(p => (
        <motion.div key={p.id}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1.2 }}
          animate={{ opacity: 0, x: p.x, y: p.y, scale: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, delay: p.delay, ease: 'easeOut' }}
          className="absolute left-1/2 top-1/2 pointer-events-none z-10"
          style={{ width: p.size, height: p.size, backgroundColor: p.color, borderRadius: p.id % 3 === 0 ? '0' : '50%' }}
        />
      ))}
    </AnimatePresence>
  );
}


interface HabitCardProps {
  habit: Habit;
  count: number;
  isCurrentWeek: boolean;
  expanded: boolean;
  celebrating: boolean;
  onToggleExpand: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onLogTime: () => void;
  onAddMinutes: (mins: number) => void;
}

function HabitCard({ habit, count, isCurrentWeek, expanded, celebrating, onToggleExpand, onIncrement, onDecrement, onLogTime, onAddMinutes }: HabitCardProps) {
  const isTime = habit.unit === 'minutes';
  const isYesNo = habit.unit === 'yes_no';
  const hit = count >= habit.target_per_week;
  const pct = Math.min((count / habit.target_per_week) * 100, 100);

  const displayCount = getDisplayCount(habit.unit, count);
  const displayTarget = getDisplayTarget(habit);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('rounded-xl border flex flex-col relative overflow-hidden transition-colors', hit ? 'border-emerald-300/60 bg-emerald-50/40' : 'border-border bg-card')}
    >
      <CelebrationBurst show={celebrating} />

      <div className="p-4 pb-3">
        {/* Card header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0 pr-2">
            <p className="font-bold text-foreground text-sm leading-snug">{buildDynamicTitle(habit)}</p>
            {habit.description && (
              <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{habit.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-sm font-bold text-foreground">{displayCount}</span>
            <span className="text-xs text-muted-foreground">/{displayTarget}</span>
            {habit.help_text && (
              <button onClick={onToggleExpand} className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground ml-1">
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-3">
          <div className={cn('h-full rounded-full transition-all duration-300', hit ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${pct}%` }} />
        </div>

        {/* Controls */}
        {isCurrentWeek ? (
          <div className="flex items-center justify-between">
            {isTime ? (
              <div className="space-y-2">
                <div className="flex gap-1 flex-wrap">
                  {[30, 60, 90, 120].map(m => (
                    <button key={m} onClick={() => onAddMinutes(m)}
                      className="text-[11px] px-2 py-1 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-medium">
                      +{m >= 60 ? `${m / 60}h` : `${m}m`}
                    </button>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={onLogTime} className="gap-1.5 text-xs h-7">
                  <Clock className="w-3 h-3" /> Custom
                </Button>
              </div>
            ) : isYesNo ? (
              <Button size="sm" variant={hit ? 'secondary' : 'outline'}
                onClick={() => hit ? onDecrement() : onIncrement()}
                className="gap-1.5 text-xs h-8">
                {hit ? <><RotateCcw className="w-3 h-3" /> Undo</> : <><Check className="w-3 h-3" /> Mark Yes</>}
              </Button>
            ) : (
              <div className="inline-flex items-center border border-border rounded-lg overflow-hidden">
                <button onClick={onDecrement} disabled={count === 0}
                  className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="h-8 w-10 flex items-center justify-center text-sm font-bold border-x border-border bg-muted/30">{count}</span>
                <button onClick={onIncrement}
                  className="h-8 w-8 flex items-center justify-center text-primary hover:bg-primary/10 transition-colors">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {hit && (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Done!
              </motion.div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Past week -- read only</p>
        )}
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && habit.help_text && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 border-t border-border/50 pt-3">
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 leading-relaxed">
                {habit.help_text}
              </p>
              {isTime && isCurrentWeek && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {[30, 60, 90, 120].map(m => (
                    <button key={m} onClick={() => onAddMinutes(m)}
                      className="text-xs px-2.5 py-1 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-medium">
                      +{formatMinutes(m)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

interface LogTimeModalProps {
  habit: Habit;
  currentCount: number;
  onLog: (minutes: number) => void;
  onClose: () => void;
}

export function LogTimeModal({ habit, currentCount, onLog, onClose }: LogTimeModalProps) {
  const [input, setInput] = useState('');
  const parsed = parseTimeInput(input);
  const isValid = parsed > 0;

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif">Log Time: {habit.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Input
              placeholder="e.g. 30 min, 1.5 hrs, 1:30"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && isValid) { onLog(parsed); onClose(); } }}
              autoFocus
            />
            {input && (
              <p className={cn('text-xs mt-1.5', isValid ? 'text-primary font-medium' : 'text-destructive')}>
                {isValid ? `= ${formatMinutes(parsed)} will be added` : 'Could not parse -- try "30 min" or "1.5 hrs"'}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {[30, 60, 90, 120].map(m => (
              <Button key={m} size="sm" variant="outline" onClick={() => { onLog(m); onClose(); }} className="text-xs">
                +{formatMinutes(m)}
              </Button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">
            Current: {formatMinutes(currentCount)} / {formatMinutes(habit.target_per_week)}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={!isValid} onClick={() => { onLog(parsed); onClose(); }} className="gradient-gold text-primary-foreground shadow-gold">
              Log Time
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function HabitSettingsRow({ habit, canRemove, onEdit, onRemove }: {
  habit: Habit; canRemove: boolean; onEdit: () => void; onRemove: () => void;
}) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={habit}
      dragControls={controls}
      dragListener={false}
      as="div"
      className="rounded-lg border border-border bg-background p-3"
    >
      <div className="flex items-center gap-2">
        <button
          onPointerDown={e => controls.start(e)}
          className="p-0.5 rounded hover:bg-muted text-muted-foreground transition-colors cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{habit.title}</p>
          <p className="text-xs text-muted-foreground">{habit.target_per_week}{unitSuffix(habit.unit)}/wk</p>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors text-xs font-medium">
            Edit
          </button>
          {canRemove && (
            <button onClick={onRemove} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </Reorder.Item>
  );
}

interface SettingsPanelProps {
  habits: Habit[];
  weekStartDay: number;
  onUpdateWeekStartDay: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void;
  onReorderFull: (reorderedHabits: Habit[]) => Promise<void>;
  onUpdateHabit: (id: string, updates: Partial<Pick<Habit, 'title' | 'description' | 'target_per_week' | 'unit'>>) => void;
  onAddHabit: (title: string, desc: string, target: number, unit: Habit['unit']) => void;
  onRemoveHabit: (id: string) => void;
  onReset: () => void;
  onClose: () => void;
}

export function SettingsPanel({ habits, weekStartDay, onUpdateWeekStartDay, onReorderFull, onUpdateHabit, onAddHabit, onRemoveHabit, onReset, onClose }: SettingsPanelProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editVals, setEditVals] = useState<{ title: string; description: string; target_per_week: number; unit: Habit['unit'] }>({ title: '', description: '', target_per_week: 1, unit: 'count' });
  const [addingHabit, setAddingHabit] = useState(false);
  const [newHabit, setNewHabit] = useState({ title: '', description: '', target: 3, unit: 'count' as Habit['unit'] });

  const startEdit = (h: Habit) => {
    setEditingId(h.id);
    setEditVals({ title: h.title, description: h.description, target_per_week: h.target_per_week, unit: h.unit });
  };

  const saveEdit = () => {
    if (editingId && editVals.title.trim()) {
      onUpdateHabit(editingId, { title: editVals.title.trim(), description: editVals.description.trim(), target_per_week: editVals.target_per_week, unit: editVals.unit });
    }
    setEditingId(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="mb-6 rounded-xl border border-border bg-card p-5 space-y-6"
    >
      {/* Week start day */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Week Starts On</p>
        <div className="flex flex-wrap gap-1.5">
          {[0, 1, 2, 3, 4, 5, 6].map(d => (
            <button key={d} onClick={() => onUpdateWeekStartDay(d as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
              className={cn('px-3 py-1.5 rounded-md text-xs font-medium transition-colors border', d === weekStartDay ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted text-foreground')}>
              {DAY_LABELS[d]}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">This setting also affects your Weekly One start and end date.</p>
      </div>

      {/* Habit list */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Your Habits</p>
        <Reorder.Group
          axis="y"
          as="div"
          values={habits}
          onReorder={onReorderFull}
          className="space-y-2"
        >
          {habits.map((h) => (
            editingId === h.id ? (
              <Reorder.Item key={h.id} value={h} as="div" dragListener={false} className="rounded-lg border border-border bg-background p-3">
                <div className="space-y-2">
                  <Input value={editVals.title} onChange={e => setEditVals(v => ({ ...v, title: e.target.value }))} placeholder="Habit name" className="text-sm" autoFocus />
                  <Input value={editVals.description} onChange={e => setEditVals(v => ({ ...v, description: e.target.value }))} placeholder="Description" className="text-sm" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Target:</span>
                    <Input type="number" min={1} value={editVals.target_per_week}
                      onChange={e => setEditVals(v => ({ ...v, target_per_week: parseInt(e.target.value) || 1 }))}
                      className="w-16 text-sm" />
                    <select value={editVals.unit} onChange={e => setEditVals(v => ({ ...v, unit: e.target.value as Habit['unit'] }))}
                      className="text-xs border border-border rounded-md px-2 py-1.5 bg-background">
                      {UNIT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <div className="ml-auto flex gap-1.5">
                      <Button size="sm" onClick={saveEdit} className="h-7 gap-1 gradient-gold text-primary-foreground shadow-gold"><Check className="w-3 h-3" /> Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7"><X className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </div>
              </Reorder.Item>
            ) : (
              <HabitSettingsRow
                key={h.id}
                habit={h}
                canRemove={habits.length > 1}
                onEdit={() => startEdit(h)}
                onRemove={() => onRemoveHabit(h.id)}
              />
            )
          ))}
        </Reorder.Group>

        {/* Add habit */}
        {addingHabit ? (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
            className="mt-2 rounded-lg border border-dashed border-primary/40 bg-background p-3 space-y-2">
            <Input value={newHabit.title} onChange={e => setNewHabit(p => ({ ...p, title: e.target.value }))} placeholder="Habit name" className="text-sm" autoFocus />
            <Input value={newHabit.description} onChange={e => setNewHabit(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="text-sm" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Target:</span>
              <Input type="number" min={1} value={newHabit.target}
                onChange={e => setNewHabit(p => ({ ...p, target: parseInt(e.target.value) || 1 }))} className="w-16 text-sm" />
              <select value={newHabit.unit} onChange={e => setNewHabit(p => ({ ...p, unit: e.target.value as Habit['unit'] }))}
                className="text-xs border border-border rounded-md px-2 py-1.5 bg-background">
                {UNIT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="ml-auto flex gap-1.5">
                <Button size="sm" disabled={!newHabit.title.trim()} onClick={() => { onAddHabit(newHabit.title, newHabit.description, newHabit.target, newHabit.unit); setNewHabit({ title: '', description: '', target: 3, unit: 'count' }); setAddingHabit(false); }} className="h-7 gradient-gold text-primary-foreground shadow-gold">Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setAddingHabit(false)} className="h-7">Cancel</Button>
              </div>
            </div>
          </motion.div>
        ) : (
          <button onClick={() => setAddingHabit(true)} className="mt-2 w-full rounded-lg border border-dashed border-muted-foreground/30 p-2.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors flex items-center justify-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add Custom Habit
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <button onClick={onReset} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <RotateCcw className="w-3 h-3" /> Reset to defaults
        </button>
        <Button size="sm" onClick={onClose} className="gradient-gold text-primary-foreground shadow-gold h-8 gap-1.5">
          <Check className="w-3.5 h-3.5" /> Done
        </Button>
      </div>
    </motion.div>
  );
}

export default function WeeklyActionTracker() {
  const { session } = useAuth();
  const { streakCount: weekly1Streak, currentCommitment, cardState, openSetModal, markComplete: markWeeklyOneComplete, daysUntilCutoff } = useWeeklyOne();
  const {
    habits, logs, loading, setupError,
    currentWeek, selectedWeek, setSelectedWeek, isCurrentWeek, availableWeeks,
    weekProgress,
    weekStartDay, updateWeekStartDay,
    getCountForWeek, increment, decrement, logMinutes, addHabit,
    updateHabit, reorderHabitsToOrder, removeHabit, resetToDefaults,
  } = useHabitTracker();

  const trackHabitLogged = (habit: Habit, amount: number) => {
    if (session?.user?.id) {
      posthog.capture('habit_logged', {
        habit_title: habit.title,
        habit_unit: habit.unit,
        amount,
      });
    }
  };

  const [showSettings, setShowSettings] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [logTimeHabitId, setLogTimeHabitId] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState<Record<string, boolean>>({});
  const prevHits = useRef<Record<string, boolean>>({});

  // Close settings when navigating to a past week -- past weeks are view-only
  useEffect(() => {
    if (!isCurrentWeek) setShowSettings(false);
  }, [isCurrentWeek]);

  useEffect(() => {
    if (loading || !isCurrentWeek) return;
    habits.forEach(h => {
      const count = getCountForWeek(h.id, currentWeek);
      const isHit = count >= h.target_per_week;
      const wasHit = prevHits.current[h.id];
      if (isHit && wasHit === false) {
        setCelebrating(prev => ({ ...prev, [h.id]: true }));
        setTimeout(() => setCelebrating(prev => ({ ...prev, [h.id]: false })), 2500);
      }
      prevHits.current[h.id] = isHit;
    });
  }, [habits, logs, loading, isCurrentWeek, currentWeek, getCountForWeek]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const weekEnd = getWeekEnd(selectedWeek);
  const weekLabel = `${format(new Date(selectedWeek + 'T00:00:00'), 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
  const allComplete = habits.length > 0 && habits.every(h => getCountForWeek(h.id, selectedWeek) >= h.target_per_week);
  const logTimeHabit = logTimeHabitId ? habits.find(h => h.id === logTimeHabitId) : null;

  const habitTargetsHit = habits.filter(h => getCountForWeek(h.id, currentWeek) >= h.target_per_week).length;
  const wo_isDone = cardState === 'completed';
  const wo_hasCommitment = !!currentCommitment?.commitment_text &&
    (cardState === 'active' || cardState === 'final-day' || cardState === 'completed');
  const wo_text = currentCommitment?.commitment_text ?? '';
  const wo_weekEnd = currentCommitment?.week_end ? new Date(currentCommitment.week_end + 'T00:00:00') : null;
  const todayDow = new Date().getDay();
  const daysUntilNextWeek = ((weekStartDay - todayDow + 7) % 7) || 7;

  return (
    <div className="mb-8">

      {/* ── Weekly Stats (3 cards) ── */}
      <section style={{ marginBottom: 28 }}>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
          letterSpacing: '.22em', textTransform: 'uppercase', color: WAT.gold,
          marginBottom: 12,
        }}>Weekly Stats</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">

          {/* Card 1: Weekly One (dark) */}
          <div style={{
            position: 'relative', borderRadius: 16, padding: '16px 18px',
            color: '#EFEAE2',
            background: `linear-gradient(135deg, ${WAT.deep} 0%, ${WAT.brown2} 60%, ${WAT.brown3} 100%)`,
            overflow: 'hidden', boxShadow: '0 6px 20px rgba(32,25,23,0.20)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              position: 'absolute', top: -50, right: -50, width: 180, height: 180,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(192,157,108,0.45) 0%, rgba(192,157,108,0) 70%)',
            }} />
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              color: WAT.goldMid, marginBottom: 8, position: 'relative',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{
                  fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
                  letterSpacing: '.22em', textTransform: 'uppercase',
                }}>Your Weekly One</span>
                <WeeklyOneInfoTooltip
                  iconClassName=""
                  style={{ color: 'rgba(192,157,108,0.55)' }}
                />
              </div>
              <Target style={{ width: 14, height: 14 }} />
            </div>
            <div style={{
              fontFamily: 'Playfair Display, serif', fontSize: 19, fontWeight: 600,
              lineHeight: 1.22, position: 'relative', color: '#EFEAE2',
              letterSpacing: '-.005em', flex: 1, marginBottom: 6, minHeight: 48,
            }}>
              {wo_hasCommitment
                ? wo_text
                : <span style={{ color: 'rgba(239,234,226,0.5)', fontStyle: 'italic', fontSize: 15 }}>
                    No commitment set for this week
                  </span>
              }
            </div>
            {!wo_isDone && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 10, position: 'relative',
              }}>
                {wo_weekEnd && (
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: 'rgba(239,234,226,0.62)' }}>
                    Due {format(wo_weekEnd, 'EEEE, MMM d')}
                  </div>
                )}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 9999,
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 600,
                  color: 'rgba(239,234,226,0.80)',
                }}>
                  {'⏱'} {daysUntilCutoff > 0 ? `${daysUntilCutoff} day${daysUntilCutoff !== 1 ? 's' : ''} left` : 'Last day'}
                </div>
              </div>
            )}
            <div style={{
              display: 'flex', alignItems: wo_isDone ? 'flex-start' : 'center', gap: 10,
              justifyContent: 'space-between', position: 'relative',
              marginTop: wo_isDone ? 10 : 0,
            }}>
              {!wo_hasCommitment ? (
                <button onClick={openSetModal} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 11px', borderRadius: 7, background: WAT.goldLight, color: WAT.deep,
                  border: 'none', fontFamily: 'Inter, sans-serif', fontSize: 11,
                  fontWeight: 700, cursor: 'pointer', letterSpacing: '.02em',
                }}>
                  <Plus style={{ width: 11, height: 11 }} /> Set your Weekly One
                </button>
              ) : wo_isDone ? (
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', width: '100%', gap: 10 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '7px 13px', borderRadius: 7, alignSelf: 'flex-start',
                      background: 'rgba(29,158,117,0.32)', color: '#5ADB9E',
                      border: '1px solid rgba(29,158,117,0.45)',
                      fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 700,
                    }}>
                      <Check style={{ width: 13, height: 13 }} /> Completed
                    </span>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: 'rgba(239,234,226,0.55)' }}>
                      {`${daysUntilNextWeek} ${daysUntilNextWeek === 1 ? 'day' : 'days'} until next week`}
                    </div>
                  </div>
                  <button onClick={openSetModal} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '6px 11px', borderRadius: 7, background: WAT.goldLight, color: WAT.deep,
                    border: 'none', fontFamily: 'Inter, sans-serif', fontSize: 11,
                    fontWeight: 700, cursor: 'pointer', letterSpacing: '.02em', whiteSpace: 'nowrap',
                  }}>
                    Set next week's →
                  </button>
                </div>
              ) : (
                <button onClick={() => { markWeeklyOneComplete(); }} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 11px', borderRadius: 7, background: WAT.goldLight, color: WAT.deep,
                  border: 'none', fontFamily: 'Inter, sans-serif', fontSize: 11,
                  fontWeight: 700, cursor: 'pointer', letterSpacing: '.02em',
                }}>
                  <Check style={{ width: 11, height: 11 }} /> Mark as done
                </button>
              )}
            </div>
          </div>

          {/* Card 2: Habit completion */}
          <div style={{
            padding: '16px 18px', borderRadius: 16,
            background: '#fff', border: `1px solid ${WAT.border}`,
            boxShadow: '0 2px 8px rgba(180,140,90,0.06)',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <span style={{
              fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
              letterSpacing: '.22em', textTransform: 'uppercase', color: WAT.gold,
            }}>Habit Completion</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
              <WATRing value={weekProgress} />
              <div>
                <div style={{
                  fontFamily: 'Playfair Display, serif', fontSize: 36, fontWeight: 700,
                  color: WAT.deep, lineHeight: 1, letterSpacing: '-.01em',
                }}>
                  {weekProgress}
                  <span style={{ fontSize: 20, fontWeight: 600, color: WAT.gold, marginLeft: 2 }}>%</span>
                </div>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: WAT.muted, marginTop: 3 }}>
                  habit completion
                </div>
              </div>
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: WAT.muted }}>
              {habitTargetsHit} of {habits.length} habit targets hit this week
            </div>
          </div>

          {/* Card 3: Weekly One streak */}
          <div style={{
            padding: '16px 18px', borderRadius: 16,
            background: '#fff', border: `1px solid ${WAT.border}`,
            boxShadow: '0 2px 8px rgba(180,140,90,0.06)',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <span style={{
              fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700,
              letterSpacing: '.22em', textTransform: 'uppercase', color: WAT.gold,
            }}>Weekly One Streak</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
              <span style={{
                fontSize: 40, lineHeight: 1,
                filter: weekly1Streak === 0 ? 'grayscale(1) opacity(0.4)' : 'none',
              }}>🔥</span>
              <div>
                <div style={{
                  fontFamily: 'Playfair Display, serif', fontSize: 36, fontWeight: 700,
                  color: weekly1Streak === 0 ? WAT.muted : WAT.deep, lineHeight: 1, letterSpacing: '-.01em',
                }}>
                  {weekly1Streak}
                </div>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: WAT.muted, marginTop: 3 }}>
                  Weekly One streak
                </div>
              </div>
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: WAT.muted, lineHeight: 1.5 }}>
              {weekly1Streak === 0
                ? 'Submit and complete your Weekly One each week to build your streak.'
                : weekly1Streak === 1
                ? 'First Weekly One complete. Stack another next week.'
                : weekly1Streak < 4
                ? `${weekly1Streak} weeks in a row. Keep the momentum.`
                : weekly1Streak < 12
                ? `${weekly1Streak}-week streak. You're building a real habit.`
                : `${weekly1Streak} weeks. This is who you are now.`
              }
            </div>
          </div>

        </div>
      </section>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-serif text-lg font-bold text-foreground flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary" />
          Weekly Action Tracker
        </h3>
        <div className="flex items-center gap-2">
          {weekly1Streak > 0 && (
            <span title={`${weekly1Streak}-week Weekly One streak`} className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold text-sm flex items-center gap-1.5">
              🔥 {weekly1Streak}-week streak
            </span>
          )}
          {allComplete && isCurrentWeek && (
            <span className="px-3 py-1 rounded-full bg-primary/15 text-primary font-semibold text-sm">Week Complete</span>
          )}
          {isCurrentWeek && (
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(s => !s)} title={showSettings ? 'Close settings' : 'Settings'} className={showSettings ? 'text-primary' : 'text-muted-foreground'}>
              <Settings className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-4">Track your weekly habits -- resets each week</p>

      {/* Settings panel */}
      <AnimatePresence>
        {showSettings && (
          <SettingsPanel
            habits={habits}
            weekStartDay={weekStartDay}
            onUpdateWeekStartDay={updateWeekStartDay}
            onReorderFull={reorderHabitsToOrder}
            onUpdateHabit={updateHabit}
            onAddHabit={addHabit}
            onRemoveHabit={removeHabit}
            onReset={resetToDefaults}
            onClose={() => setShowSettings(false)}
          />
        )}
      </AnimatePresence>

      {/* Week navigation */}
      <div className="flex items-center gap-2 mb-5">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
          const idx = availableWeeks.indexOf(selectedWeek);
          if (idx < availableWeeks.length - 1) setSelectedWeek(availableWeeks[idx + 1]);
        }}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <button className="px-3 py-1 rounded-md border border-border bg-card text-sm font-medium min-w-[160px] text-center hover:bg-muted transition-colors">
              {weekLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-52 p-1 max-h-64 overflow-y-auto" align="center">
            {availableWeeks.map(ws => {
              const end = getWeekEnd(ws);
              const lbl = `${format(new Date(ws + 'T00:00:00'), 'MMM d')} - ${format(end, 'MMM d')}`;
              return (
                <button key={ws} onClick={() => setSelectedWeek(ws)}
                  className={cn('w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                    ws === selectedWeek ? 'bg-primary/15 text-primary font-semibold' : 'hover:bg-muted text-foreground')}>
                  {lbl}{ws === currentWeek && <span className="ml-1 text-xs text-muted-foreground">(current)</span>}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isCurrentWeek}
          onClick={() => {
            const idx = availableWeeks.indexOf(selectedWeek);
            if (idx > 0) setSelectedWeek(availableWeeks[idx - 1]);
          }}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        {!isCurrentWeek && (
          <Button variant="outline" size="sm" onClick={() => setSelectedWeek(currentWeek)} className="text-xs">
            Back to this week
          </Button>
        )}
      </div>

      {/* Habit cards or empty state */}
      {habits.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center mb-8">
          <p className="text-muted-foreground text-sm mb-1">
            {setupError ? 'Could not load your habits. Check your connection and try again.' : 'No habits set up yet.'}
          </p>
          <p className="text-xs text-muted-foreground mb-4">Click below to load your default weekly action habits.</p>
          <Button size="sm" className="gradient-gold shadow-gold text-white" onClick={() => resetToDefaults()}>
            Set Up My Habits
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {habits.map(habit => (
              <HabitCard
                key={habit.id}
                habit={habit}
                count={getCountForWeek(habit.id, selectedWeek)}
                isCurrentWeek={isCurrentWeek}
                expanded={expandedId === habit.id}
                celebrating={!!celebrating[habit.id]}
                onToggleExpand={() => setExpandedId(id => id === habit.id ? null : habit.id)}
                onIncrement={() => { increment(habit.id); trackHabitLogged(habit, 1); }}
                onDecrement={() => decrement(habit.id)}
                onLogTime={() => setLogTimeHabitId(habit.id)}
                onAddMinutes={(mins) => { logMinutes(habit.id, mins); trackHabitLogged(habit, mins); }}
              />
            ))}
          </div>

          {/* Log time modal */}
          {logTimeHabit && (
            <LogTimeModal
              habit={logTimeHabit}
              currentCount={getCountForWeek(logTimeHabit.id, currentWeek)}
              onLog={(mins) => { logMinutes(logTimeHabit.id, mins); trackHabitLogged(logTimeHabit, mins); }}
              onClose={() => setLogTimeHabitId(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
